import React, { useState, useEffect } from "react";
import { IReservation } from "../Interfaces/IReservation";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    MenuItem,
    InputLabel,
    FormControl,
    Skeleton,
    Typography,
    Divider,
    Stack,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useGetDrinks } from "../hooks/drinks";
import { useGetCleaningFees } from "../hooks/cleaningFees";
import { useGetReservationPrice } from "../hooks/reservationsPrice";
import Box from "@mui/material/Box";
import { primaryButtonSx, secondaryButtonSx } from "../core/pvtaStyles";

interface ReservationInvoiceProps {
    reservation: IReservation;
    open: boolean;
    onClose: () => void;
    onSubmit: (data: InvoiceData) => void;
    initialInvoice?: {
        purpose: string;
        cleaning_fee_id: number;
        overnight_items: { price_item_id: number; quantity: number }[];
        drink_items: { drink_id: number; quantity: number }[];
    } | null;
}

export interface InvoiceData {
    counts: Record<number, number>;
    drinkCounts: Record<number, number>;
    cleaningFeeId: number;
    total: number;
}

const InvoiceFieldSkeleton = () => (
    <Grid container spacing={2}>
        {[0, 1, 2, 3].map((item) => (
            <Grid item xs={6} sm={4} md={3} key={item}>
                <Skeleton variant="rounded" height={40} />
            </Grid>
        ))}
    </Grid>
);

export const ReservationInvoice: React.FC<ReservationInvoiceProps> = ({
                                                                          reservation,
                                                                          open,
                                                                          onClose,
                                                                          onSubmit,
                                                                          initialInvoice
                                                                      }) => {
    const { reservationPriceArray, isLoadingReservationPriceArray } = useGetReservationPrice();
    const { drinkArray, isLoadingDrinkArray } = useGetDrinks();
    const { cleaningFeeArray, isLoadingCleaningFeeArray } = useGetCleaningFees();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [counts, setCounts] = useState<Record<number, number>>({});
    const [drinkCounts, setDrinkCounts] = useState<Record<number, number>>({});
    const [selectedCleaningFeeId, setSelectedCleaningFeeId] = useState<number>(0);



    useEffect(() => {
        if (!isLoadingReservationPriceArray && reservationPriceArray.length) {
            const initial: Record<number, number> = reservationPriceArray.reduce((acc, p) => {
                const existing = initialInvoice?.overnight_items.find(i => i.price_item_id === p.id);
                return { ...acc, [p.id]: existing?.quantity ?? 0 };
            }, {});
            setCounts(initial);
        }
    }, [reservationPriceArray, isLoadingReservationPriceArray, initialInvoice]);

    useEffect(() => {
        if (!isLoadingDrinkArray && drinkArray.length) {
            const initial: Record<number, number> = drinkArray.reduce((acc, d) => {
                const existing = initialInvoice?.drink_items.find(i => i.drink_id === d.id);
                return { ...acc, [d.id]: existing?.quantity ?? 0 };
            }, {});
            setDrinkCounts(initial);
        }
    }, [drinkArray, isLoadingDrinkArray, initialInvoice]);

    useEffect(() => {
        if (!isLoadingCleaningFeeArray && cleaningFeeArray.length) {
            const initialId = initialInvoice?.cleaning_fee_id ?? cleaningFeeArray[0].id;
            setSelectedCleaningFeeId(initialId);
        }
    }, [cleaningFeeArray, isLoadingCleaningFeeArray, initialInvoice]);

    const handleCountChange = (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = Math.max(0, parseInt(event.target.value, 10) || 0);
        setCounts(prev => ({ ...prev, [id]: value }));
    };

    const handleDrinkChange = (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = Math.max(0, parseInt(event.target.value, 10) || 0);
        setDrinkCounts(prev => ({ ...prev, [id]: value }));
    };

    const handleCleaningFeeChange = (event: SelectChangeEvent<number>) => {
        setSelectedCleaningFeeId(event.target.value as number);
    };

    const calculateTotal = (): number => {
        let total = 0;
        reservationPriceArray.forEach(p => {
            total += (counts[p.id] || 0) * Number(p.price);
        });
        drinkArray.forEach(d => {
            total += (drinkCounts[d.id] || 0) * Number(d.price);
        });
        const fee = cleaningFeeArray.find(f => f.id === selectedCleaningFeeId);
        if (fee) total += Number(fee.price);
        return total;
    };

    const formattedTotal = calculateTotal().toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

    const handleSubmit = () => {
        onSubmit({ counts, drinkCounts, cleaningFeeId: selectedCleaningFeeId, total: calculateTotal() });
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0px 20px 45px rgba(15, 23, 42, 0.16)',
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {initialInvoice ? "Rechnung bearbeiten" : "Rechnung erstellen"}
                    </Typography>
                    {initialInvoice?.purpose && (
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                            {initialInvoice.purpose}
                        </Typography>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Stack spacing={3}>
                    <div>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                            Übernachtungszahlen
                        </Typography>
                        {isLoadingReservationPriceArray ? (
                            <InvoiceFieldSkeleton />
                        ) : (
                            <Grid container spacing={2}>
                                {reservationPriceArray.map(p => (
                                    <Grid item xs={6} sm={4} md={3} key={p.id}>
                                        <TextField
                                            label={`${p.name} (${Number(p.price).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })})`}
                                            type="number"
                                            inputProps={{ min: 0 }}
                                            fullWidth
                                            value={counts[p.id] || 0}
                                            onChange={handleCountChange(p.id)}
                                            size="small"
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </div>

                    <Divider />

                    <div>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                            Getränke
                        </Typography>
                        {isLoadingDrinkArray ? (
                            <InvoiceFieldSkeleton />
                        ) : (
                            <Grid container spacing={2}>
                                {drinkArray.map(d => (
                                    <Grid item xs={6} sm={4} md={3} key={d.id}>
                                        <TextField
                                            label={`${d.name} (${Number(d.price).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })})`}
                                            type="number"
                                            inputProps={{ min: 0 }}
                                            fullWidth
                                            value={drinkCounts[d.id] || 0}
                                            onChange={handleDrinkChange(d.id)}
                                            size="small"
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </div>

                    <Divider />

                    <div>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                            Reinigungspauschale
                        </Typography>
                        {isLoadingCleaningFeeArray ? (
                            <Skeleton variant="rounded" height={40} />
                        ) : (
                            <FormControl fullWidth>
                                <InputLabel id="cleaning-fee-label">Gebühr wählen</InputLabel>
                                <Select
                                    labelId="cleaning-fee-label"
                                    value={selectedCleaningFeeId}
                                    label="Gebühr wählen"
                                    onChange={handleCleaningFeeChange}
                                    size="small"
                                >
                                    {cleaningFeeArray.map(f => (
                                        <MenuItem key={f.id} value={f.id}>
                                            {`${f.name} (${Number(f.price).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })})`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </div>

                    <Divider />

                    <Typography variant="h6" align="right" sx={{ fontWeight: 700 }}>
                        Gesamt: {formattedTotal}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={secondaryButtonSx}>Abbrechen</Button>
                <Button onClick={handleSubmit} sx={{ ...primaryButtonSx }}>
                    {initialInvoice ? "Rechnung aktualisieren" : "Rechnung erstellen"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReservationInvoice;
